<template>
  <base-slide-over ref="slideOver" :title="`${isEditing ? 'Edit' : 'New'} Integration`" size="xl" style="z-index: 10000">
    <base-form ref="form" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6" @submit.prevent>
      <div class="tw-w-24 tw-h-24 sm:tw-w-32 sm:tw-h-32 tw-mb-4 tw-mx-auto" v-if="isEditing">
        <avatar :integration-id="integrationId" editable avatarType="integration" @image-uploaded="$emit('image-uploaded')" />
      </div>
      <base-text-field label="Name" placeholder="Enter name" v-model="name" required />
      <base-textarea label="Description" placeholder="Enter description" v-model="description" required />
      <base-currency-input label="Price / Month" placeholder="Enter price per month" :int-value.sync="pricePerMonthInt" v-model="pricePerMonth" required />
      <div class="tw-relative tw-mt-2 tw-mb-4">
        <div class="tw-absolute tw-inset-0 tw-flex tw-items-center">
          <div class="tw-w-full tw-border-t tw-border-gray-300" />
        </div>
        <div class="tw-relative tw-flex tw-justify-center tw-text-sm">
          <span class="tw-px-2 tw-bg-white tw-text-gray-600 tw-font-thin">
            Attributes
          </span>
        </div>
      </div>
      <div v-for="(attribute, index) in attributes" :key="index" :class="['tw-flex tw-relative tw-items-center', index + 1 < attributes.length ? 'tw-mb-10' : '']">
        <div class="tw-w-1 tw-h-full tw-bg-primary tw-mr-2 tw-rounded-full tw-pt-1 tw-top-0 tw-bottom-0 tw-absolute" />
        <div class="tw-ml-3 tw-mx-3 tw-flex-grow">
          <base-text-field label="Attribute Name" placeholder="Enter attribute name" v-model="attribute.name" class="tw-mb-5" />
          <base-textarea label="Attribute Description" placeholder="Enter attribute description" v-model="attribute.description" />
        </div>
        <div>
          <base-icon color="red" @click="removeAttribute(index)"><x-icon class="tw-w-5 tw-h-5"/></base-icon>
        </div>
      </div>
      <div class="tw-flex tw-justify-center tw-mt-4">
        <base-button type="flat" size="xs" @click="addAttribute">
          <template v-slot:icon>
            <plus-icon />
          </template>
          Add
        </base-button>
      </div>
    </base-form>
    <template #fixed-bottom>
      <div class="tw-p-3 tw-flex tw-justify-end">
        <base-button type="flat" class="tw-w-auto tw-mr-2" @click="$refs.slideOver.hide()">
          Cancel
        </base-button>
        <base-button class="tw-w-auto" :loading="isLoading" @click="create">
          <template v-slot:icon>
            <component :is="isEditing ? 'check-icon' : 'plus-icon'" />
          </template>
          {{ isEditing ? "Update" : "Create" }}
        </base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import Avatar from "@/components/Avatar";
import { PlusIcon, XIcon, CheckIcon } from "@/assets/svgs";

const defaultAttributes = [{ name: "", description: "" }];

export default {
  components: {
    Avatar,
    PlusIcon,
    XIcon,
    CheckIcon,
  },
  data() {
    return {
      isLoading: false,
      isEditing: false,
      integrationId: null,
      name: null,
      description: null,
      pricePerMonth: null,
      pricePerMonthInt: null,
      attributes: defaultAttributes,
    };
  },
  methods: {
    show(integration) {
      this.$refs.slideOver.show();
      if (integration) {
        this.integrationId = integration.integrationId;
        this.name = integration.name;
        this.description = integration.description;
        this.pricePerMonth = integration.priceInCents;
        this.attributes = integration.attributes.length ? integration.attributes : defaultAttributes;
        this.isEditing = true;
      } else {
        this.integrationId = null;
        this.name = null;
        this.description = null;
        this.pricePerMonth = null;
        this.attributes = defaultAttributes;
        this.isEditing = false;
        this.reset();
      }
    },
    hide() {
      this.$refs.slideOver.hide();
      this.reset();
    },
    reset() {
      this.integrationId = null;
      this.name = null;
      this.description = null;
      this.pricePerMonth = null;
      this.attributes = defaultAttributes;
      this.isEditing = false;
    },
    addAttribute() {
      this.attributes.push({ name: "", description: "" });
    },
    removeAttribute(index) {
      this.attributes.splice(index, 1);
    },
    async create() {
      if (!this.$refs.form.validate()) return;
      try {
        this.isLoading = true;
        const body = {
          name: this.name,
          description: this.description,
          priceInCents: this.pricePerMonthInt,
          currency: "usd",
          attributes: this.attributes.filter((a) => a.name || a.description),
        };
        if (this.isEditing) {
          await this.$axios.put(`/integrations/${this.integrationId}`, body);
          this.$emit("integration-updated", {
            ...body,
            integrationId: this.integrationId,
          });
          this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Integration Updated", message: "The integration was successfully updated" });
        } else {
          const response = await this.$axios.post(`/integrations`, body);
          this.$emit("integration-updated", response.data);
          this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Integration Created", message: "The integration was successfully created" });
        }
        this.hide();
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: `Failed to ${this.isEditing ? "update" : "create"} the integration`,
          message: `We had a problem ${this.isEditing ? "updating" : "creating"} the integration. Please reload the page to try again.`,
          error,
        });
      } finally {
        this.isLoading = false;
        this.reset();
      }
    },
  },
};
</script>
